import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { AuditFormData } from "@/pages/AuditFlow";
import AuditProgress from "./AuditProgress";

interface AuditStep2Props {
  data: Partial<AuditFormData>;
  onNext: (data: Partial<AuditFormData>) => void;
  onBack: () => void;
}

export default function AuditStep2({ data, onNext, onBack }: AuditStep2Props) {
  const [processStandardization, setProcessStandardization] = useState<number>(
    data.process_standardization || 3
  );
  const [tools, setTools] = useState<string[]>(data.tools || []);
  const [manualHours, setManualHours] = useState(data.manual_hours || "");
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    setIsValid(!!manualHours);
  }, [manualHours]);

  const toolOptions = [
    "CRM",
    "Helpdesk",
    "Marketing Automation",
    "Data Warehouse",
    "None/Other",
  ];

  const toggleTool = (tool: string) => {
    setTools((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    );
  };

  const handleNext = () => {
    if (isValid) {
      onNext({
        process_standardization: processStandardization,
        tools,
        manual_hours: manualHours,
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <AuditProgress currentStep={2} totalSteps={3} />

      <div className="bg-dark-card rounded-2xl p-8 md:p-12 border border-border/20 mt-8">
        <h2 className="text-3xl font-bold text-foreground mb-3">
          Current State
        </h2>
        <p className="text-light-gray/80 mb-8">
          Tell us about your current operations
        </p>

        <div className="space-y-8">
          {/* Process Standardization */}
          <div>
            <Label className="text-foreground mb-4 block">
              How standardized are your lead workflows? *
            </Label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-medium-gray min-w-[100px]">
                Not at all
              </span>
              <Slider
                value={[processStandardization]}
                onValueChange={(value) => setProcessStandardization(value[0])}
                min={1}
                max={5}
                step={1}
                className="flex-1"
              />
              <span className="text-sm text-medium-gray min-w-[100px] text-right">
                Highly standardized
              </span>
            </div>
            <div className="text-center mt-2">
              <span className="text-2xl font-bold text-cyan">
                {processStandardization}
              </span>
              <span className="text-sm text-light-gray/70"> / 5</span>
            </div>
          </div>

          {/* Tools */}
          <div>
            <Label className="text-foreground mb-3 block">
              Which tools are in play? (Select all that apply)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {toolOptions.map((tool) => (
                <div
                  key={tool}
                  className="flex items-center space-x-2 bg-background p-3 rounded-lg border border-border hover:border-cyan/40 transition-colors cursor-pointer"
                  onClick={() => toggleTool(tool)}
                >
                  <Checkbox
                    id={tool}
                    checked={tools.includes(tool)}
                    onCheckedChange={() => toggleTool(tool)}
                  />
                  <label
                    htmlFor={tool}
                    className="text-sm text-foreground cursor-pointer flex-1"
                  >
                    {tool}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Manual Hours */}
          <div>
            <Label htmlFor="manual_hours" className="text-foreground mb-2 block">
              Manual follow-up hours per week? *
            </Label>
            <Select value={manualHours} onValueChange={setManualHours}>
              <SelectTrigger id="manual_hours" className="bg-background border-border">
                <SelectValue placeholder="Select hours range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="<2">Less than 2 hours</SelectItem>
                <SelectItem value="2-10">2-10 hours</SelectItem>
                <SelectItem value="10-25">10-25 hours</SelectItem>
                <SelectItem value="25+">25+ hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-between mt-10">
          <Button
            onClick={onBack}
            variant="outline"
            className="border-border text-foreground hover:bg-dark-card"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!isValid}
            className="bg-cyan hover:bg-cyan-muted text-deep-navy font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}